// File: frontend/src/pages/Reinsurance/contexts/ContractContext.js

import React, { createContext, useContext, useState, useCallback } from 'react';
import { reinsuranceService } from '../../../services/reinsurance';

const ContractContext = createContext();

export const useContract = () => useContext(ContractContext);

export const ContractProvider = ({ children }) => {
  const [contracts, setContracts] = useState([]);
  const [currentContract, setCurrentContract] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchContracts = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const response = await reinsuranceService.listContracts(filters);
      
      if (response.success) {
        setContracts(response.data);
        return response.data;
      } else {
        setError(response.message || 'Failed to fetch contracts');
        return [];
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching contracts');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchContractById = useCallback(async (contractId) => {
    setLoading(true);
    try {
      const response = await reinsuranceService.getContract(contractId);
      
      if (response.success) {
        setCurrentContract(response.data);
        return response.data;
      } else {
        setError(response.message || 'Failed to fetch contract details');
        return null;
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching contract details');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createContract = useCallback(async (contractData) => {
    setLoading(true);
    try {
      const response = await reinsuranceService.createContract(contractData);
      
      if (response.success) {
        await fetchContracts();
        return { success: true, data: response.data };
      } else {
        setError(response.message || 'Failed to create contract');
        return { success: false, error: response.message };
      }
    } catch (err) {
      setError(err.message || 'An error occurred while creating contract');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [fetchContracts]);

  const updateContract = useCallback(async (contractId, contractData) => {
    setLoading(true);
    try {
      const response = await reinsuranceService.updateContract(contractId, contractData);
      
      if (response.success) {
        if (currentContract && currentContract.contract_id === contractId) {
          setCurrentContract(response.data);
        }
        await fetchContracts();
        return { success: true, data: response.data };
      } else {
        setError(response.message || 'Failed to update contract');
        return { success: false, error: response.message };
      }
    } catch (err) {
      setError(err.message || 'An error occurred while updating contract');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [fetchContracts, currentContract]);

  const verifyRNR = useCallback(async (rnr) => {
    try {
      const response = await reinsuranceService.verifyRNR(rnr);
      return response;
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const value = {
    contracts,
    currentContract,
    loading,
    error,
    fetchContracts,
    fetchContractById,
    createContract,
    updateContract,
    verifyRNR,
    clearCurrentContract: () => setCurrentContract(null),
    clearError: () => setError(null)
  };

  return (
    <ContractContext.Provider value={value}>
      {children}
    </ContractContext.Provider>
  );
};